import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Lock, Download, Truck, AlertCircle, Building2, Calendar, CheckCircle2, XCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const VT = { camion: "Camión", panel: "Panel", pickup: "Pickup", furgon: "Furgón", cisterna: "Cisterna", otro: "Otro" };

export default function PublicTransportReport() {
    const { reportId } = useParams();
    const [meta, setMeta] = useState(null);
    const [pin, setPin] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/public/transport/${reportId}/meta`).then((r) => setMeta(r.data)).catch(() => setError("Reporte no encontrado"));
    }, [reportId]);

    const submit = async (val) => {
        const p = (val || pin).trim();
        if (p.length !== 4) return;
        setLoading(true);
        try {
            const r = await api.post(`/public/transport/${reportId}/verify-pin`, { pin: p });
            setData(r.data);
        } catch {
            toast.error("PIN incorrecto");
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
                <Card className="p-8 text-center max-w-sm w-full">
                    <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
                    <p className="font-semibold text-lg">Reporte no encontrado</p>
                    <p className="text-sm text-muted-foreground mt-1">El enlace podría haber expirado o ser inválido.</p>
                </Card>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4">
                <Card className="p-8 max-w-sm w-full text-center" data-testid="transport-pin-form">
                    <BrandLogo className="h-8 mx-auto mb-4" />
                    <Truck className="w-10 h-10 mx-auto text-[#CC0000] mb-3" />
                    <h2 className="text-lg font-bold mb-1">Control de Transportes</h2>
                    {meta && (
                        <div className="flex flex-col items-center gap-1 mb-4">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Building2 className="w-4 h-4" /> {meta.client_name}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Calendar className="w-4 h-4" /> {meta.service_date}
                            </div>
                        </div>
                    )}
                    <Lock className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-4">Ingrese el código de 4 dígitos</p>
                    <div className="flex justify-center mb-4">
                        <InputOTP maxLength={4} value={pin} onChange={setPin} onComplete={submit}>
                            <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                            </InputOTPGroup>
                        </InputOTP>
                    </div>
                    <Button onClick={() => submit()} disabled={pin.length !== 4 || loading} className="w-full bg-[#CC0000] text-white" data-testid="transport-pin-submit">
                        {loading ? "Verificando..." : "Verificar"}
                    </Button>
                </Card>
            </div>
        );
    }

    const report = data.report;
    const treatments = report.treatments || [];
    const total = treatments.length;
    const treated = treatments.filter((t) => t.service_done).length;

    return (
        <div className="min-h-screen bg-zinc-50 py-6 px-4" data-testid="transport-public-report">
            <div className="max-w-2xl mx-auto">
                <div className="text-center mb-4">
                    <BrandLogo className="h-8 mx-auto mb-2" />
                    <h1 className="text-xl font-bold flex items-center justify-center gap-2">
                        <Truck className="w-5 h-5 text-[#CC0000]" /> Control de Transportes
                    </h1>
                    <p className="text-sm text-muted-foreground">{data.client?.name} — {report.service_date}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Card className="p-3 text-center">
                        <div className="text-2xl font-bold">{total}</div>
                        <div className="text-[10px] uppercase text-muted-foreground">Total</div>
                    </Card>
                    <Card className="p-3 text-center bg-green-50 border-green-200">
                        <div className="text-2xl font-bold text-green-700">{treated}</div>
                        <div className="text-[10px] uppercase text-green-600">Tratados</div>
                    </Card>
                    <Card className="p-3 text-center bg-orange-50 border-orange-200">
                        <div className="text-2xl font-bold text-orange-600">{total - treated}</div>
                        <div className="text-[10px] uppercase text-orange-500">Sin tratar</div>
                    </Card>
                </div>

                {/* Treatments list */}
                <Card className="mb-4 overflow-hidden">
                    {treatments.map((t, i) => (
                        <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t" : ""}`}>
                            <div className="flex items-center gap-3">
                                {t.service_done
                                    ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                                    : <XCircle className="w-5 h-5 text-orange-500 shrink-0" />
                                }
                                <div>
                                    <span className="font-mono font-bold text-sm">{t.plate_number}</span>
                                    <Badge variant="outline" className="ml-2 text-[10px]">{VT[t.vehicle_type] || t.vehicle_type}</Badge>
                                    <p className="text-xs text-muted-foreground">{t.brand}{t.tonnage ? ` · ${t.tonnage}` : ""}</p>
                                </div>
                            </div>
                            {t.products_applied?.length > 0 && (
                                <span className="text-xs text-muted-foreground">{t.products_applied.map((p) => p.product_name).join(", ")}</span>
                            )}
                        </div>
                    ))}
                </Card>

                {report.general_observations && (
                    <Card className="p-4 mb-4 bg-amber-50 border-amber-200">
                        <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Observaciones</p>
                        <p className="text-sm">{report.general_observations}</p>
                    </Card>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                    <Card className="p-3">
                        <p className="text-xs text-muted-foreground">Realizado por</p>
                        <p className="font-semibold">{report.performed_by || data.technician?.full_name || "—"}</p>
                    </Card>
                    <Card className="p-3">
                        <p className="text-xs text-muted-foreground">Aceptado por</p>
                        <p className="font-semibold">{report.accepted_by || "—"}</p>
                    </Card>
                </div>

                {report.pdf_url && (
                    <a href={assetUrl(report.pdf_url)} target="_blank" rel="noreferrer" className="block">
                        <Button className="w-full h-12 bg-[#CC0000] hover:bg-[#CC0000]/90 text-white font-semibold" data-testid="transport-download-pdf">
                            <Download className="w-5 h-5 mr-2" /> Descargar reporte PDF
                        </Button>
                    </a>
                )}
            </div>
        </div>
    );
}
